import { Group } from '@mantine/core';
import { DateInput } from '@mantine/dates';
import type { ReactNode } from 'react';
import { useT } from '../i18n/useT.js';
import { toIsoDate } from '../format.js';

/**
 * A from/to window, shared by the reports and the SAF-T export.
 *
 * One component because the three pages that need it must agree on what a
 * period means. Both ends are **inclusive**, matching the API and the VAT
 * periods elsewhere in the system — a report whose end date silently excluded
 * the last day would understate the final month of every quarter.
 *
 * Either end may be empty. The API reads an absent bound as "no bound", which
 * is a useful thing to ask for — a balance sheet as at a date has no beginning
 * — rather than a state to prevent.
 */

export interface Period {
  readonly from: Date | null;
  readonly to: Date | null;
}

/** The period as the API wants it: `YYYY-MM-DD`, and absent when unset. */
export function toQuery(period: Period): { from?: string; to?: string } {
  return {
    ...(period.from === null ? {} : { from: toIsoDate(period.from) }),
    ...(period.to === null ? {} : { to: toIsoDate(period.to) }),
  };
}

export interface PeriodPickerProps {
  readonly value: Period;
  readonly onChange: (period: Period) => void;
  /** Labels, so a page can say what the window means where "from" is wrong. */
  readonly fromLabel?: string;
  readonly toLabel?: string;
}

export function PeriodPicker({
  value,
  onChange,
  fromLabel,
  toLabel,
}: PeriodPickerProps): ReactNode {
  const t = useT();

  return (
    <Group align="flex-end" wrap="wrap">
      <DateInput
        label={fromLabel ?? t('period.fromLabel')}
        placeholder={t('period.from')}
        valueFormat="DD.MM.YYYY"
        clearable
        maw={200}
        value={value.from}
        onChange={(next) => {
          onChange({ ...value, from: next === null ? null : new Date(next) });
        }}
      />
      <DateInput
        label={toLabel ?? t('period.toLabel')}
        placeholder={t('period.to')}
        valueFormat="DD.MM.YYYY"
        clearable
        maw={200}
        value={value.to}
        onChange={(next) => {
          onChange({ ...value, to: next === null ? null : new Date(next) });
        }}
      />
    </Group>
  );
}
